'use client'
import { clamp } from '@polaris/core/utils'
import { memo, useRef } from 'react'
import { cn } from '../utils/styles'

interface RadialProgressProps {
  /**
   * Specify if radial progress should be animated
   */
  animated?: boolean
  /**
   * Start animation timestamp in ms
   */
  startTime?: number
  /**
   * End animation timestamp in ms
   */
  endTime?: number
  /**
   * Animation duration in seconds
   */
  duration?: number
  strokeWidth?: number
  svgClasses?: string
  circleClasses?: string
  progressClasses?: string
}

const radialOffset = 6 // reduce perimeter to always leave a small gap at the end

const RadialProgress = memo<RadialProgressProps>(
  ({
    animated = false,
    startTime: _startTime,
    endTime: _endTime,
    duration: _duration,
    strokeWidth = 1,
    svgClasses,
    circleClasses,
    progressClasses,
  }: RadialProgressProps) => {
    const currentTime = useRef(Date.now())
    const startTime = useRef(_startTime ? _startTime : Date.now())
    const endTime = useRef(
      _endTime ? _endTime : Date.now() + (_duration ?? 0) * 1000,
    )

    const elapsed = (currentTime.current - startTime.current) / 1000
    const duration = (endTime.current - startTime.current) / 1000

    const strokeOffset = Math.ceil(strokeWidth / 2)
    const radius = 25 - strokeOffset
    const perimeter = 2 * Math.PI * radius
    // Calculate progress based on elapsed time proportion
    const progressOffset = clamp(elapsed / duration, 0, 1)
    const progressStart = Math.max(0, perimeter * progressOffset - radialOffset)

    return (
      <svg
        className={cn('h-full w-full', svgClasses)}
        viewBox='0 0 50 50'
        style={{
          // @ts-ignore
          '--progress-start': progressStart,
          '--progress-end': perimeter,
        }}
      >
        <circle
          className={circleClasses}
          strokeWidth={strokeWidth}
          stroke='currentColor'
          fill='transparent'
          r={radius}
          cx='25'
          cy='25'
        />
        <circle
          className={cn(
            'origin-[50%_50%] -rotate-90 transform animate-progress text-surface3-100',
            progressClasses,
          )}
          style={{
            animation: !animated
              ? 'unset'
              : `progress ${duration}s ease-in-out`,
            animationPlayState: !animated ? 'paused' : 'running',
          }}
          strokeWidth={strokeWidth}
          strokeDasharray={perimeter}
          strokeLinecap='round'
          stroke='currentColor'
          fill='transparent'
          r={radius}
          cx='25'
          cy='25'
        />
      </svg>
    )
  },
)
RadialProgress.displayName = 'RadialProgress'

export { RadialProgress }
